import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Music2 } from "lucide-react";
import { useAppData } from "@/context/AppData";
import { api, track } from "@/lib/api";
import { STEPS, faqs, windowLabel } from "@/lib/content";
import { Page } from "@/components/site/Layout";
import { OccasionBrowser } from "@/components/site/OccasionBrowser";
import { AudioPlayer } from "@/components/site/Audio";
import { PriceTag, PriceCTA } from "@/components/site/PriceTag";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function Occasions() {
  const { catalog } = useAppData();
  const navigate = useNavigate();
  useEffect(() => track("occasion_browsed"), []);
  return (
    <Page eyebrow="Browse occasions" title="What are you celebrating?" intro="Find the right moment, or describe your own with Custom Occasion." testid="occasions-page">
      {catalog && <OccasionBrowser catalog={catalog} onSelect={(o) => { track("occasion_selected", { occasion_id: o.id }); navigate(`/create?occasion=${o.id}`); }} />}
    </Page>
  );
}

export function Pricing() {
  const { config } = useAppData();
  if (!config) return null;
  return (
    <Page eyebrow="Pricing" title="One song. One simple price." testid="pricing-page">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="card-white p-10">
          <p className="eyebrow">{config.product_name}</p>
          <div className="mt-4"><PriceTag config={config} testid="pricing-price" /></div>
          <p className="mt-2 text-sm text-[var(--plum-muted)]">One-time payment via PayPal · {config.currency}</p>
          <ul className="mt-8 space-y-3">
            {config.inclusions.map((i) => <li key={i} className="flex gap-3"><Check className="h-5 w-5 shrink-0 text-[var(--gold-deep)]" aria-hidden />{i}</li>)}
          </ul>
          <PriceCTA config={config} className="mt-8" testid="pricing-page-cta" />
        </div>
        <div className="space-y-6 text-[var(--plum-2)]">
          <div><h2 className="font-display text-2xl text-[var(--plum-text)]">Revisions</h2><p className="mt-2">{config.revision_count} revision request within {windowLabel(config.revision_window_days)} of delivery. {config.revision_covers}</p><p className="mt-2 text-sm">Not included: {config.revision_excludes}</p></div>
          <div><h2 className="font-display text-2xl text-[var(--plum-text)]">Delivery</h2><p className="mt-2">{config.fulfillment_window || "Your song is generated after payment and checked by our team before delivery. We don't promise delivery by a specific date."}</p></div>
          <div><h2 className="font-display text-2xl text-[var(--plum-text)]">How songs are made</h2><p className="mt-2">{config.ai_disclosure}</p></div>
        </div>
      </div>
    </Page>
  );
}

export function HowItWorks() {
  return (
    <Page eyebrow="How it works" title="Thoughtful from first memory to final note" testid="how-page">
      <ol className="grid gap-6 md:grid-cols-2">
        {STEPS.map((s, i) => (
          <li key={s.title} className="card-parch p-8">
            <span className="font-display text-4xl text-[var(--gold)]">0{i + 1}</span>
            <h2 className="mt-3 text-xl font-semibold">{s.title}</h2>
            <p className="mt-2 text-[var(--plum-2)]">{s.text}</p>
          </li>
        ))}
      </ol>
      <Link to="/create" className="btn-gold mt-10">Create Their Song</Link>
    </Page>
  );
}

export function Examples() {
  const { catalog } = useAppData();
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ occasion: "", style: "", tone: "" });
  useEffect(() => { api.get("/examples").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const shown = useMemo(() => (items || []).filter((e) => (!f.occasion || e.occasion_id === f.occasion) && (!f.style || e.style_id === f.style) && (!f.tone || e.tone === f.tone)), [items, f]);
  const sel = (key, label, opts) => (
    <label className="block min-w-[160px] flex-1"><span className="label">{label}</span>
      <select className="field" value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={`examples-filter-${key}`}>
        <option value="">All</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select></label>
  );
  return (
    <Page eyebrow="Example songs" title="Listen before you create" intro="Every example here is published with permission and approved by our team." testid="examples-page">
      {catalog && <div className="flex flex-wrap gap-4">
        {sel("occasion", "Occasion", catalog.occasions.map((o) => [o.id, o.label]))}
        {sel("style", "Musical style", catalog.styles.map((s) => [s.id, s.label]))}
        {sel("tone", "Emotional tone", catalog.tones.map((t) => [t, t]))}
      </div>}
      {items && shown.length === 0 && (
        <div className="card-parch mt-10 p-10 text-center sm:p-14" data-testid="examples-empty">
          <Music2 className="mx-auto h-8 w-8 text-[var(--gold)]" aria-hidden="true" />
          {items.length === 0 ? (
            <>
              <p className="font-display mt-4 text-2xl sm:text-3xl">An exclusive collection is on its way</p>
              <p className="mx-auto mt-3 max-w-xl text-[var(--plum-muted)]">We are curating an exclusive selection of custom songs. We respect customer privacy, and we are currently gathering approved memories to share. In the meantime, envision your own story in song!</p>
            </>
          ) : (
            <>
              <p className="font-display mt-4 text-2xl">No examples match these filters yet</p>
              <p className="mx-auto mt-3 max-w-xl text-[var(--plum-muted)]">Try another occasion, style or tone — or imagine your own story in song.</p>
            </>
          )}
          <Link to="/create" className="btn-gold mt-7" data-testid="examples-empty-cta">Create Their Song</Link>
        </div>
      )}
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {shown.map((e) => <AudioPlayer key={e.id} src={e.audio_url} title={e.title} subtitle={e.description} testid={`example-${e.id}`} />)}
      </div>
    </Page>
  );
}

export function FAQ() {
  const { config } = useAppData();
  return (
    <Page eyebrow="FAQ" title="Questions, answered" testid="faq-page">
      <Accordion type="single" collapsible className="max-w-3xl">
        {faqs(config).map(([q, a], i) => (
          <AccordionItem key={q} value={`f${i}`}>
            <AccordionTrigger className="text-left text-base" data-testid={`faq-page-q-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-[var(--plum-2)]">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Page>
  );
}

export function Legal({ kind }) {
  const { config } = useAppData();
  const titles = { privacy: "Privacy Policy", terms: "Terms", refund: "Refund & Revision Policy" };
  return (
    <Page eyebrow="Policies" title={titles[kind]} testid={`legal-${kind}`}>
      {kind !== "refund" && <p className="mb-6 inline-block rounded-full bg-[var(--gold-soft)] px-4 py-1.5 text-xs font-semibold text-[var(--gold-deep)]">Draft — pending legal review · Terms version {config?.terms_version}</p>}
      <div className="max-w-3xl whitespace-pre-line leading-relaxed text-[var(--plum-2)]">{config?.legal?.[kind]}</div>
    </Page>
  );
}
